import { useCallback, useEffect, useRef, useState } from 'react';

interface UseScrollFadeResult {
  showFade: boolean;
  scrollRef: React.RefObject<HTMLElement | null>;
}

export const useScrollFade = (deps: ReadonlyArray<unknown> = []): UseScrollFadeResult => {
  const [showFade, setShowFade] = useState(false);
  const scrollRef = useRef<HTMLElement>(null);

  const updateFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setShowFade(false);
      return;
    }
    const canScroll = el.scrollHeight > el.clientHeight;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 2;
    setShowFade(canScroll && !atBottom);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateFade();
    el.addEventListener('scroll', updateFade, { passive: true });
    const observer = new ResizeObserver(updateFade);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateFade);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateFade, ...deps]);

  return { showFade, scrollRef };
};
